import { Breadcrumb, Button, Card, Col, Divider, Form, Input, message, Popconfirm, Row, Table } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import gql from 'graphql-tag';
import React, { Component, FormEvent } from 'react';
import { Mutation, Query } from 'react-apollo';
import { Link, RouteComponentProps } from 'react-router-dom';
import { client } from 'src/services/api';

import { Panel } from '../../components/Panel';
import { AddPermission } from './AddPermission';
import { PERMISSION_ACTIONS, USER_ROLE_PERMISSIONS_QUERY } from './constant';
import { EditTableCell, EditTableContext, EditTableFormRow } from './EditTableCell';
import { UserPermission } from './type';

const AddPermissionForm = Form.create()(AddPermission);

interface State {
  currentPage: number;
  editingUserPermission?: UserPermission;

  variables: { id?: number; action?: string; resource?: string };
}

interface Props {
  form: WrappedFormUtils;
}

type AllProps = RouteComponentProps & Props;

const updateUserRolePermissionMutation = gql`
  mutation updateUserRolePermission($id: ID!, $action: String, $resource: String, $description: String) {
    updateUserRolePermission(id: $id, action: $action, resource: $resource, description: $description) {
      id
      action
      resource
      description
    }
  }
`;

const deleteUserPermissionsMutation = gql`
  mutation deleteUserRolePermission($id: ID!) {
    deleteUserRolePermission(id: $id) {
      id
      action
      resource
      description
    }
  }
`;

class PermissionListForm extends Component<AllProps, State> {
  constructor(props: AllProps) {
    super(props);
    this.state = {
      variables: {},
      currentPage: 1,
    };
  }

  search = (e: FormEvent) => {
    const variables: { id?: number; action?: string; resource?: string } = {};

    this.props.form.validateFields((err, values) => {
      if (err) {
        message.error(err);
        return;
      }
      const { permissionId, permissionAction, permissionResource } = values;
      if (permissionId && permissionId !== '') {
        variables.id = permissionId;
      }
      if (permissionAction && permissionAction !== '') {
        variables.action = permissionAction;
      }
      if (permissionAction && permissionResource !== '') {
        variables.resource = permissionResource;
      }
      this.setState({
        variables,
      });
    });
    e.preventDefault();
  };

  onResetClick = () => {
    this.props.form.resetFields();
  };

  onUpdateClick = (form: any, record: UserPermission) => {
    form.validateFields(async (error: Error, row: { action: string; resource: string; description: string }) => {
      if (error) {
        return;
      }
      const newUserPermission: UserPermission = { id: Number(record.id), ...row };
      try {
        if (!PERMISSION_ACTIONS.some(value => value === newUserPermission.action)) {
          message.error(`action的值只能为${PERMISSION_ACTIONS.toString()}`);
          return;
        }
        await client.mutate<{ userPermission: UserPermission }>({
          mutation: updateUserRolePermissionMutation,
          variables: newUserPermission,
          refetchQueries: [{ query: USER_ROLE_PERMISSIONS_QUERY }],
        });
        message.success('更新成功！');
      } catch (error) {
        message.error(`${error.message}`);
      }
      this.setState({
        editingUserPermission: undefined,
      });
    });
  };

  onCancelClick = () => {
    this.setState({ editingUserPermission: undefined });
  };

  onEditClick = (record: UserPermission) => {
    this.setState({
      editingUserPermission: record,
    });
  };

  onPageChange = (currentPage: number) => {
    this.setState({
      currentPage,
    });
  };

  render() {
    const {
      editingUserPermission,

      currentPage,
      variables,
    } = this.state;
    const columnsOrigin = [
      {
        title: '权限ID',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: '描述',
        dataIndex: 'description',
        key: 'description',
        editable: true,
      },
      {
        title: '行为',
        dataIndex: 'action',
        key: 'action',
        editable: true,
      },

      {
        title: '来源组件',
        dataIndex: 'resource',
        key: 'resource',
        editable: true,
      },
      {
        title: '操作',
        key: 'actions',
        render: (record: UserPermission) => {
          return (
            <>
              {editingUserPermission && record.id === editingUserPermission.id ? (
                <span>
                  <EditTableContext.Consumer>
                    {form => (
                      <Popconfirm title="确定更新?" onConfirm={() => this.onUpdateClick(form, record)}>
                        <Button type="primary" style={{ marginRight: 8 }}>
                          更新
                        </Button>
                      </Popconfirm>
                    )}
                  </EditTableContext.Consumer>
                  <Button type="primary" onClick={this.onCancelClick}>
                    取消
                  </Button>
                </span>
              ) : (
                <Button
                  type="primary"
                  disabled={editingUserPermission !== undefined}
                  onClick={() => this.onEditClick(record)}
                >
                  编辑
                </Button>
              )}
              <Divider type="vertical" />
              <Mutation
                mutation={deleteUserPermissionsMutation}
                variables={{ id: Number(record.id) }}
                refetchQueries={[{ query: USER_ROLE_PERMISSIONS_QUERY }]}
              >
                {(deleteUserRolePermission, { loading }) => {
                  return (
                    <>
                      <Popconfirm
                        title="是否确定删除该权限?"
                        onConfirm={async () => {
                          try {
                            await deleteUserRolePermission();
                            message.success('删除成功！');
                          } catch (error) {
                            message.error(`${error.message}`);
                          }
                        }}
                      >
                        <Button type="primary" loading={loading}>
                          删除
                        </Button>
                      </Popconfirm>
                    </>
                  );
                }}
              </Mutation>
            </>
          );
        },
      },
    ];

    const components = {
      body: {
        row: EditTableFormRow,
        cell: EditTableCell,
      },
    };
    const editColumns = columnsOrigin.map(col => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: record => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          editing: editingUserPermission && record.id === editingUserPermission.id ? 1 : 0,
        }),
      };
    });
    const { getFieldDecorator } = this.props.form;
    return (
      <div>
        <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
          <Breadcrumb.Item>
            <Link to={`/Home`}>首页</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>权限列表</Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Form onSubmit={this.search}>
            <Row gutter={24} type={'flex'} align={'middle'}>
              <Col span={8}>
                <Form.Item label={`权限ID`}>
                  {getFieldDecorator('permissionId')(<Input placeholder="请输入权限ID" allowClear />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={`权限行为`}>
                  {getFieldDecorator('permissionAction')(<Input placeholder="请输入权限行为" allowClear />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={`权限来源组件`}>
                  {getFieldDecorator('permissionResource')(<Input placeholder="请输入权限来源组件" allowClear />)}
                </Form.Item>
              </Col>
            </Row>
            <Row type="flex" justify="end">
              <Button icon="search" type="primary" style={{ marginRight: 8 }} htmlType="submit">
                搜索
              </Button>
              <Button style={{ marginRight: 8 }} type="primary" onClick={this.onResetClick}>
                重置
              </Button>
              <AddPermissionForm />
            </Row>
          </Form>
        </Card>
        <Panel style={{ marginTop: 10 }}>
          <Query query={USER_ROLE_PERMISSIONS_QUERY} variables={variables} fetchPolicy="cache-and-network">
            {({ loading, error, data }) => {
              if (error) return <div>{error.message}</div>;
              const { userRolePermissions = [] } = data || {};
              return (
                <Table<UserPermission>
                  loading={loading}
                  components={components}
                  rowClassName={() => 'editable-row'}
                  rowKey={record => `${record.id}`}
                  dataSource={userRolePermissions}
                  columns={editColumns}
                  pagination={{ current: currentPage, onChange: this.onPageChange }}
                />
              );
            }}
          </Query>
        </Panel>
      </div>
    );
  }
}

export const PermissionList = Form.create({ name: 'PermissionList' })(PermissionListForm);



// WEBPACK FOOTER //
// ./src/screens/rolePermission/PermissionList.tsx